/**
 * Common timezones grouped by region
 * Provides ~50 commonly used timezones for user selection
 */

export const TIMEZONE_GROUPS = [
  {
    label: 'Europe',
    timezones: [
      { value: 'Europe/London', label: 'London (GMT/BST)' },
      { value: 'Europe/Dublin', label: 'Dublin (GMT/IST)' },
      { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
      { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
      { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
      { value: 'Europe/Brussels', label: 'Brussels (CET/CEST)' },
      { value: 'Europe/Vienna', label: 'Vienna (CET/CEST)' },
      { value: 'Europe/Zurich', label: 'Zurich (CET/CEST)' },
      { value: 'Europe/Rome', label: 'Rome (CET/CEST)' },
      { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
      { value: 'Europe/Lisbon', label: 'Lisbon (WET/WEST)' },
      { value: 'Europe/Stockholm', label: 'Stockholm (CET/CEST)' },
      { value: 'Europe/Oslo', label: 'Oslo (CET/CEST)' },
      { value: 'Europe/Copenhagen', label: 'Copenhagen (CET/CEST)' },
      { value: 'Europe/Helsinki', label: 'Helsinki (EET/EEST)' },
      { value: 'Atlantic/Reykjavik', label: 'Reykjavik (GMT)' },
      { value: 'Europe/Warsaw', label: 'Warsaw (CET/CEST)' },
      { value: 'Europe/Prague', label: 'Prague (CET/CEST)' },
      { value: 'Europe/Athens', label: 'Athens (EET/EEST)' },
      { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
      { value: 'Europe/Istanbul', label: 'Istanbul (TRT)' }
    ]
  },
  {
    label: 'Americas',
    timezones: [
      { value: 'America/New_York', label: 'New York (EST/EDT)' },
      { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
      { value: 'America/Denver', label: 'Denver (MST/MDT)' },
      { value: 'America/Phoenix', label: 'Phoenix (MST)' },
      { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
      { value: 'America/Anchorage', label: 'Anchorage (AKST/AKDT)' },
      { value: 'Pacific/Honolulu', label: 'Honolulu (HST)' },
      { value: 'America/Toronto', label: 'Toronto (EST/EDT)' },
      { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)' },
      { value: 'America/Mexico_City', label: 'Mexico City (CST/CDT)' },
      { value: 'America/Sao_Paulo', label: 'Sao Paulo (BRT)' },
      { value: 'America/Buenos_Aires', label: 'Buenos Aires (ART)' },
      { value: 'America/Lima', label: 'Lima (PET)' },
      { value: 'America/Bogota', label: 'Bogota (COT)' }
    ]
  },
  {
    label: 'Asia & Pacific',
    timezones: [
      { value: 'Asia/Dubai', label: 'Dubai (GST)' },
      { value: 'Asia/Kolkata', label: 'India (IST)' },
      { value: 'Asia/Bangkok', label: 'Bangkok (ICT)' },
      { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
      { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
      { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
      { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
      { value: 'Asia/Seoul', label: 'Seoul (KST)' },
      { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
      { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
      { value: 'Australia/Perth', label: 'Perth (AWST)' },
      { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' }
    ]
  },
  {
    label: 'Africa & Middle East',
    timezones: [
      { value: 'Africa/Cairo', label: 'Cairo (EET)' },
      { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
      { value: 'Africa/Lagos', label: 'Lagos (WAT)' },
      { value: 'Africa/Nairobi', label: 'Nairobi (EAT)' },
      { value: 'Asia/Jerusalem', label: 'Jerusalem (IST/IDT)' },
      { value: 'Asia/Riyadh', label: 'Riyadh (AST)' }
    ]
  },
  {
    label: 'Other',
    timezones: [
      { value: 'UTC', label: 'UTC (Coordinated Universal Time)' }
    ]
  }
];

/**
 * Get all timezones as a flat array
 * @returns {Array} Array of { value, label, group } objects
 */
export function getAllTimezones() {
  const result = [];
  for (const group of TIMEZONE_GROUPS) {
    for (const tz of group.timezones) {
      result.push({
        ...tz,
        group: group.label
      });
    }
  }
  return result;
}

/**
 * Find a timezone by value
 * @param {string} value - IANA timezone value
 * @returns {Object|null} Timezone object or null
 */
export function findTimezone(value) {
  for (const group of TIMEZONE_GROUPS) {
    const tz = group.timezones.find(t => t.value === value);
    if (tz) {
      return { ...tz, group: group.label };
    }
  }
  return null;
}

/**
 * Check if a timezone is in our supported list
 * @param {string} value - IANA timezone value
 * @returns {boolean}
 */
export function isTimezoneSupported(value) {
  return findTimezone(value) !== null;
}

/**
 * Get display label for a timezone
 * @param {string} value - IANA timezone value
 * @returns {string} Display label or the value itself if not found
 */
export function getTimezoneLabel(value) {
  const tz = findTimezone(value);
  return tz ? tz.label : value;
}
